import { action, computed, observable, runInAction } from "mobx";
import * as ReactGA from "react-ga";

import AuthService, { LoginRequest } from "./AuthService";
import { setCookie } from "lib/cookie";
import RootStore from "./RootStore";

interface User {
  displayName?: string;
}

export default class AuthStore {
  @observable private user?: User | null;
  @observable private idToken?: string;

  private rootStore: RootStore;
  private authService: AuthService;

  private localTokenRefreshMin: number = 10;
  private lastTokenChecked: Date = new Date();
  private tokenTimer: number;

  constructor(rootStore: RootStore, authService: AuthService) {
    this.rootStore = rootStore;
    this.authService = authService;

    this.user = JSON.parse(localStorage.getItem("user")!);
    this.idToken = localStorage.getItem("token")!;
    this.lastTokenChecked = new Date(localStorage.getItem("lastTokenChecked")!);
    const currentDate = new Date();
    const diffDate =
      (currentDate.getTime() - new Date(this.lastTokenChecked).getTime()) /
      (1000 * 60);
    if (
      !!this.user &&
      !!this.idToken &&
      this.lastTokenChecked &&
      diffDate < this.localTokenRefreshMin
    ) {
      this.updateToken();
      this.updateUser(this.user);
    } else {
      this.updateUser(null);
    }
  }

  @computed
  get isLoggedIn(): boolean | undefined {
    if (this.user === undefined) {
      return undefined;
    }
    return !!this.user;
  }

  @computed
  get displayName(): string {
    return (this.isLoggedIn && this.user && this.user.displayName) || "";
  }

  @computed
  get token(): string | undefined {
    return this.idToken ? `JWT ${this.idToken}` : undefined;
  }

  @action.bound
  public async login(req: LoginRequest) {
    try {
      const res = await this.authService.login(req);
      if (res) {
        runInAction(() => {
          this.idToken = res.token;
          this.lastTokenChecked = new Date();
          this.updateUser({
            displayName: req.username,
          });
          this.rootStore.pageStore.openToast(`Welcome ${req.username}.`);
          ReactGA.event({
            category: "Auth",
            action: "Logged in",
            label: `Logged in as ${req.username}`,
          });
          if (req.remember) {
            setCookie("username", req.username);
            setCookie("password", req.password);
          }
        });
      }
    } catch (e) {
      this.rootStore.pageStore.openToast(e.message || "Wrong Id or Password", {
        type: "error",
      });
    }
  }

  @action.bound
  public logout(): void {
    ReactGA.event({
      category: "Auth",
      action: "Logged out",
      label: `Logged out from ${this.user!.displayName}`,
    });
    this.updateUser(undefined);
  }

  @action.bound
  private updateUser(user?: User | null) {
    if (user) {
      this.authService.refreshToken(this.idToken).then((idToken) => {
        runInAction(() => {
          this.user = user;
          this.idToken = idToken;
          this.lastTokenChecked = new Date();
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("token", idToken);
          localStorage.setItem(
            "lastTokenChecked",
            this.lastTokenChecked.toString(),
          );
          this.updateToken();
          this.tokenTimer = window.setInterval(() => {
            this.updateToken();
          }, 5000);
        });
      });
    } else {
      this.idToken = undefined;
      this.user = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("lastTokenChecked");
      clearInterval(this.tokenTimer);
    }
  }

  @action.bound
  private updateToken() {
    const currentDate = new Date();
    const diffDate =
      (currentDate.getTime() - new Date(this.lastTokenChecked).getTime()) /
      (1000 * 60);
    if (diffDate >= this.localTokenRefreshMin) {
      this.authService.refreshToken(this.idToken).then((idToken) => {
        runInAction(() => {
          this.idToken = idToken;
          this.lastTokenChecked = new Date();
          localStorage.setItem("token", idToken);
          localStorage.setItem(
            "lastTokenChecked",
            this.lastTokenChecked.toString(),
          );
        });
      });
    }
  }
}
